/**
 * Chaupar AI — Computer Opponent
 *
 * Three difficulty levels:
 * - Easy: random valid moves
 * - Medium: prioritize captures > blocks > advance
 * - Hard: scoring function with team coordination
 */

import {
  PlayerColor,
  TeamColor,
  ChauparDifficulty,
  ChauparMove,
  TurnMoveSet,
  DiceResult,
  PawnPosition,
  ChauparGameState,
  TEAMS,
  PLAYER_START_POSITIONS,
  TRACK_LENGTH,
  HOME_COLUMN_LENGTH,
  PAWNS_PER_PLAYER,
  NUM_DICE,
} from './types';
import { ChauparEngine } from './ChauparEngine';

export class ChauparAI {
  private difficulty: ChauparDifficulty;
  private engine: ChauparEngine;

  constructor(difficulty: ChauparDifficulty, engine: ChauparEngine) {
    this.difficulty = difficulty;
    this.engine = engine;
  }

  selectMoves(player: PlayerColor, diceResult: DiceResult): TurnMoveSet {
    switch (this.difficulty) {
      case 'easy':
        return this.selectMovesEasy(player, diceResult);
      case 'medium':
        return this.selectMovesMedium(player, diceResult);
      case 'hard':
        return this.selectMovesHard(player, diceResult);
      default:
        return this.selectMovesEasy(player, diceResult);
    }
  }

  private selectMovesEasy(player: PlayerColor, _diceResult: DiceResult): TurnMoveSet {
    const moves: ChauparMove[] = [];

    for (let dieIndex = 0; dieIndex < NUM_DICE; dieIndex++) {
      const validMoves = this.engine.getValidMoves(player).filter(
        m => m.dieIndex === dieIndex && !moves.some(used => used.dieIndex === dieIndex)
      );

      if (validMoves.length === 0) continue;

      // Filter out skips if there are real moves
      const realMoves = validMoves.filter(m => m.action !== 'skip');
      if (realMoves.length > 0) {
        const randomIndex = Math.floor(Math.random() * realMoves.length);
        const selectedMove = realMoves[randomIndex];
        moves.push(selectedMove);
        // Execute the move so subsequent getValidMoves reflects it
        this.engine.executeMove(player, selectedMove);
      } else {
        // Only skips available
        const skipMove = validMoves.find(m => m.action === 'skip');
        if (skipMove) {
          moves.push(skipMove);
          this.engine.executeMove(player, skipMove);
        }
      }
    }

    return { moves };
  }

  private selectMovesMedium(player: PlayerColor, _diceResult: DiceResult): TurnMoveSet {
    const moves: ChauparMove[] = [];

    for (let dieIndex = 0; dieIndex < NUM_DICE; dieIndex++) {
      const validMoves = this.engine.getValidMoves(player).filter(
        m => m.dieIndex === dieIndex && !moves.some(used => used.dieIndex === dieIndex)
      );

      if (validMoves.length === 0) continue;

      const realMoves = validMoves.filter(m => m.action !== 'skip');
      if (realMoves.length === 0) {
        const skipMove = validMoves.find(m => m.action === 'skip');
        if (skipMove) {
          moves.push(skipMove);
          this.engine.executeMove(player, skipMove);
        }
        continue;
      }

      // Priority: capture > block > enter > advance closest to home
      const selectedMove = this.selectByPriority(player, realMoves);
      moves.push(selectedMove);
      this.engine.executeMove(player, selectedMove);
    }

    return { moves };
  }

  private selectMovesHard(player: PlayerColor, _diceResult: DiceResult): TurnMoveSet {
    const moves: ChauparMove[] = [];

    for (let dieIndex = 0; dieIndex < NUM_DICE; dieIndex++) {
      const validMoves = this.engine.getValidMoves(player).filter(
        m => m.dieIndex === dieIndex && !moves.some(used => used.dieIndex === dieIndex)
      );

      if (validMoves.length === 0) continue;

      const realMoves = validMoves.filter(m => m.action !== 'skip');
      if (realMoves.length === 0) {
        const skipMove = validMoves.find(m => m.action === 'skip');
        if (skipMove) {
          moves.push(skipMove);
          this.engine.executeMove(player, skipMove);
        }
        continue;
      }

      // Score each move
      let bestMove = realMoves[0];
      let bestScore = -Infinity;

      for (const move of realMoves) {
        const score = this.evaluateMove(move, player);
        if (score > bestScore) {
          bestScore = score;
          bestMove = move;
        }
      }

      moves.push(bestMove);
      this.engine.executeMove(player, bestMove);
    }

    return { moves };
  }

  private selectByPriority(player: PlayerColor, moves: ChauparMove[]): ChauparMove {
    const state = this.engine.getGameState();

    // 1. Capture moves
    for (const move of moves) {
      if (this.wouldCapture(player, move, state)) {
        return move;
      }
    }

    // 2. Block formation moves
    for (const move of moves) {
      if (this.wouldFormBlock(player, move, state)) {
        return move;
      }
    }

    // 3. Advance closest to home
    const moveMoves = moves.filter(m => m.action === 'move');
    if (moveMoves.length > 0) {
      return this.getClosestToHomeMove(player, moveMoves, state);
    }

    // Fallback: first available
    return moves[0];
  }

  private evaluateMove(move: ChauparMove, player: PlayerColor): number {
    const state = this.engine.getGameState();
    let score = 0;

    // Capture: highest priority (100)
    if (this.wouldCapture(player, move, state)) {
      score += 100;
      // Extra points for capturing pawns near their home
      const captureTarget = this.getCaptureTarget(player, move, state);
      if (captureTarget) {
        const targetDist = this.getDistanceToHome(captureTarget.player, captureTarget.position);
        score += Math.max(0, 50 - targetDist); // More points for pawns closer to home
      }
    }

    // Block formation with partner (80)
    if (this.wouldFormBlock(player, move, state)) {
      score += 80;
    }

    // Home column progress (60)
    if (move.action === 'move') {
      const pawn = state.pawns[player][move.pawnIndex];
      if (pawn.location === 'home_column') {
        score += 60 + (pawn.homeColumnPosition || 0) * 5;
      }
    }

    // Blocking opponent near their home (50)
    if (this.wouldBlockOpponent(player, move, state)) {
      score += 50;
    }

    // Safety: avoid isolated positions (40)
    score += this.scoreSafety(move, player, state) * 40;

    // Team coordination (30)
    score += this.scoreTeamCoordination(move, player, state) * 30;

    // General advancement (10)
    if (move.action === 'move') {
      score += 10 + move.dieValue;
    }

    return score;
  }

  private wouldCapture(player: PlayerColor, move: ChauparMove, state: ChauparGameState): boolean {
    if (move.action === 'skip') return false;

    const destPos = this.getMoveDestination(player, move, state);
    if (destPos === null) return false;

    const occupants = this.getOccupantsAt(destPos, state);
    const playerTeam = this.getTeamForPlayer(player);
    const opponents = occupants.filter(o => this.getTeamForPlayer(o.player) !== playerTeam);

    return opponents.length === 1 && occupants.length === 1;
  }

  private getCaptureTarget(player: PlayerColor, move: ChauparMove, state: ChauparGameState): { player: PlayerColor; position: PawnPosition } | null {
    const destPos = this.getMoveDestination(player, move, state);
    if (destPos === null) return null;

    const occupants = this.getOccupantsAt(destPos, state);
    const playerTeam = this.getTeamForPlayer(player);
    const opponents = occupants.filter(o => this.getTeamForPlayer(o.player) !== playerTeam);

    if (opponents.length === 1) {
      return { player: opponents[0].player, position: state.pawns[opponents[0].player][opponents[0].pawnIndex] };
    }
    return null;
  }

  private wouldFormBlock(player: PlayerColor, move: ChauparMove, state: ChauparGameState): boolean {
    if (move.action === 'skip') return false;

    const destPos = this.getMoveDestination(player, move, state);
    if (destPos === null) return false;

    const occupants = this.getOccupantsAt(destPos, state);
    const playerTeam = this.getTeamForPlayer(player);
    const teammates = occupants.filter(o => this.getTeamForPlayer(o.player) === playerTeam);

    return teammates.length >= 1;
  }

  private wouldBlockOpponent(_player: PlayerColor, _move: ChauparMove, _state: ChauparGameState): boolean {
    // Simplified: check if move lands near opponent pawns that are close to home
    return false;
  }

  private scoreSafety(move: ChauparMove, player: PlayerColor, state: ChauparGameState): number {
    if (move.action !== 'move') return 0;

    const destPos = this.getMoveDestination(player, move, state);
    if (destPos === null) return 0;

    // Check if there's a teammate nearby for potential block
    const occupants = this.getOccupantsAt(destPos, state);
    const playerTeam = this.getTeamForPlayer(player);
    const teammates = occupants.filter(o =>
      this.getTeamForPlayer(o.player) === playerTeam &&
      !(o.player === player && o.pawnIndex === move.pawnIndex)
    );

    return teammates.length > 0 ? 1 : 0;
  }

  private scoreTeamCoordination(move: ChauparMove, player: PlayerColor, state: ChauparGameState): number {
    if (move.action === 'skip') return 0;

    const destPos = this.getMoveDestination(player, move, state);
    if (destPos === null) return 0;

    // Check proximity to partner's pawns
    const playerTeam = this.getTeamForPlayer(player);
    const partner = TEAMS[playerTeam].find(p => p !== player);
    if (!partner) return 0;

    const partnerPawns = state.pawns[partner].filter(p => p.location === 'track');
    for (const pp of partnerPawns) {
      const dist = Math.abs((pp.trackPosition || 0) - destPos);
      if (dist <= 2) return 1; // Close to partner
    }

    return 0;
  }

  private getMoveDestination(player: PlayerColor, move: ChauparMove, state: ChauparGameState): number | null {
    if (move.action === 'move') {
      const pawn = state.pawns[player][move.pawnIndex];
      if (pawn.location === 'track') {
        return (pawn.trackPosition! + move.dieValue) % TRACK_LENGTH;
      }
    }
    return null;
  }

  private getOccupantsAt(position: number, state: ChauparGameState): { player: PlayerColor; pawnIndex: number }[] {
    const occupants: { player: PlayerColor; pawnIndex: number }[] = [];
    for (const color of ['red', 'blue', 'green', 'yellow'] as PlayerColor[]) {
      for (const pawn of state.pawns[color]) {
        if (pawn.location === 'track' && pawn.trackPosition === position) {
          occupants.push({ player: color, pawnIndex: pawn.pawnIndex });
        }
      }
    }
    return occupants;
  }

  private getClosestToHomeMove(player: PlayerColor, moves: ChauparMove[], state: ChauparGameState): ChauparMove {
    let bestMove = moves[0];
    let bestDist = Infinity;

    for (const move of moves) {
      const pawn = state.pawns[player][move.pawnIndex];
      const dist = this.getDistanceToHome(player, pawn);
      if (dist < bestDist) {
        bestDist = dist;
        bestMove = move;
      }
    }

    return bestMove;
  }

  private getDistanceToHome(player: PlayerColor, pawn: PawnPosition): number {
    if (pawn.location === 'home') return 0;
    if (pawn.location === 'eliminated') return Infinity;
    if (pawn.location === 'home_column') {
      return HOME_COLUMN_LENGTH - (pawn.homeColumnPosition || 0);
    }
    if (pawn.location === 'track') {
      const startPos = PLAYER_START_POSITIONS[player];
      const currentPos = pawn.trackPosition || 0;
      let distTraveled: number;
      if (currentPos >= startPos) {
        distTraveled = currentPos - startPos;
      } else {
        distTraveled = TRACK_LENGTH - startPos + currentPos;
      }
      return TRACK_LENGTH - distTraveled + HOME_COLUMN_LENGTH;
    }
    return Infinity;
  }

  private getTeamForPlayer(player: PlayerColor): TeamColor {
    if (TEAMS.teamA.includes(player)) return 'teamA';
    return 'teamB';
  }
}
